import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import * as css from './TimetablePage.css';
import TimetableToolbar from './components/timetableToolbar/TimetableToolbar';
import TimetableBoard from './components/timetableBoard/TimetableBoard';
import ManageLecturePopup from './components/manageLecturePopup/ManageLecturePopup';
import AddItemMenu from './components/addItemMenu/AddItemMenu';
import {getTimetable} from '../../redux/timetable/timetableActions';
import setGlobalState from '../../redux/setGlobalState';
import {RootState} from '../../redux/store.type';
import {TTimetableItem} from '../../redux/timetable/timetable.type';
import {useTimetableDays} from './hooks/useTimetableDays';
import {useBreakPopup} from './hooks/useBreakPopup';
import useShow from '../../hooks/useShow';
import PageWrapper from '../components/pageWrapper/PageWrapper';
import ViewToggle from '../../assets/icons/ViewToggle';
import PlusThin from '../../assets/icons/PlusThin';
import ConferenceDates from '../../assets/icons/ConferenceDates';
import DownloadReport from '../../assets/icons/DownloadReport';

function TimetablePage() {
    const {t} = useTranslation('timetable');
    const navigate = useNavigate();
    const eid = useSelector((state: RootState) => state.event.eid);
    const [expanded, setExpanded] = useState(true);
    const days = useTimetableDays();
    const hasDate = days.currentDayIndex !== -1;
    const lectureFormShow = useShow();
    const [currentLecture, setCurrentLecture] = useState<TTimetableItem | null>(null);
    const [defaultTsid, setDefaultTsid] = useState<number | null>(null);
    const {handleAddBreak, handleBreakEdit, popup: breakPopup} = useBreakPopup();

    const openCreate = (tsid: number | null = null) => {
        setCurrentLecture(null);
        setDefaultTsid(tsid);
        lectureFormShow.open();
    };
    const openEdit = (lecture: TTimetableItem) => {
        setCurrentLecture(lecture);
        setDefaultTsid(null);
        lectureFormShow.open();
    };
    const closeForm = async (e?: Event | React.SyntheticEvent) => {
        await lectureFormShow.close(e);
        setCurrentLecture(null);
        setDefaultTsid(null);
    };

    useEffect(() => {
        if(eid) getTimetable();
    }, [eid]);

    useEffect(() => {
        return () => setGlobalState('timetable.currentDayIndex', 0);
    }, []);

    const actions = {
        toggleView: {label: t('toggleView'), onClick: () => setExpanded(e => !e), icon: <ViewToggle />},
        downloadReport: {label: t('downloadLecturesReport'), onClick: () => {}, icon: <DownloadReport />, disabled: true},
        conferenceDates: {label: t('conferenceDates'), onClick: () => navigate(`/e/${eid}/event`), icon: <ConferenceDates />},
        addLecture: {label: t('addLecture'), onClick: () => openCreate(), icon: <PlusThin />},
        addBreak: {label: t('addBreak'), onClick: handleAddBreak, icon: <PlusThin />},
    };
    const buttons = [
        {key: 0, children: actions.toggleView.icon, onClick: actions.toggleView.onClick, withoutPlus: true, variant: 'subtle'},
        {key: 1, children: actions.downloadReport.icon, text: actions.downloadReport.label, onClick: actions.downloadReport.onClick, disabled: actions.downloadReport.disabled, withoutPlus: true, variant: 'neutral'},
        {key: 2, children: actions.conferenceDates.icon, text: actions.conferenceDates.label, onClick: actions.conferenceDates.onClick, withoutPlus: true, variant: 'neutral'},
        {key: 'divider', Comp: () => <css.toolbarDivider />},
        {key: 3, Comp: () => <AddItemMenu text={t('addItem')} variant="neutral" placement="below" iconStart={<PlusThin />} onAddLecture={() => openCreate()} onAddBreak={hasDate ? handleAddBreak : undefined} disableBreak={!hasDate} />},
        {key: 4, children: actions.addLecture.icon, text: actions.addLecture.label, onClick: actions.addLecture.onClick, withoutPlus: true, variant: 'brand'},
    ];
    const routing = {rightContent: <TimetableToolbar />};
    const mobileActions = [
        {key: 5, onClick: days.onPrevDay, text: t('prevDay'), withoutPlus: true},
        {key: 6, text: days.currentDateLabel, withoutPlus: true, list: {text: days.currentDateLabel, arr: days.conferenceDays.map((day: {key: string; label: string}, i: number) => ({text: day.label, key: String(i), onClick: () => days.onDaySelect(i)}))}},
        {key: 7, onClick: days.onNextDay, text: t('nextDay'), withoutPlus: true},
        {key: 8, onClick: actions.toggleView.onClick, text: actions.toggleView.label, withoutPlus: true},
        {key: 9, onClick: actions.addLecture.onClick, text: actions.addLecture.label, withoutPlus: true},
        ...(hasDate ? [{key: 13, onClick: actions.addBreak.onClick, text: actions.addBreak.label, withoutPlus: true}] : []),
        {key: 11, onClick: actions.conferenceDates.onClick, text: actions.conferenceDates.label, withoutPlus: true},
        {key: 12, onClick: actions.downloadReport.onClick, text: actions.downloadReport.label, withoutPlus: true},
    ];

    return (
        <>
            {/* @ts-expect-error JS component has no TS types */}
            <PageWrapper operations={{buttons, isNewButton: true}} mobileActions={mobileActions} routing={routing} subMenuProps={{wrapCssStyle: '&.changeCss { justify-content: space-between; }'}}>
                <TimetableBoard
                    expanded={expanded}
                    days={days}
                    conferenceDatesAction={actions.conferenceDates}
                    onAddLecture={openCreate}
                    onEditLecture={openEdit}
                    onAddBreak={handleAddBreak}
                    onBreakEdit={handleBreakEdit}
                />
            </PageWrapper>
            <ManageLecturePopup show={{...lectureFormShow, close: closeForm}} lecture={currentLecture} defaultTsid={defaultTsid} />
            {breakPopup}
        </>
    );
}

export default TimetablePage;
